import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {booleanStringZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";
import type {WorkspaceTransferUiConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {type WorkspaceUsersConfiguration, workspaceUsersConfigurationValidator} from "@vesta/vos-types/modules/workspaceLevel/users/domain/WorkspaceUsersConfiguration";

/**
 * Workspace UI configuration JSON (scaffold, pages, forms, CTAs, …).
 * Nested section shapes live in `WorkspaceConfig`.
 *
 * Do **not** store row/workspace identity here — those live on
 * `workspace_ui_config.id` / `workspace_ui_config.workspace_id` so the same
 * configuration JSON can be reused across workspaces.
 *
 * Unknown keys (including legacy `id` / `workspaceId`) are stripped on parse.
 */
export type WorkspaceUiConfigConfiguration = {
    columnMapping: Record<string, unknown>;
    components?: Record<string, unknown>;
    ctas: Record<string, unknown>;
    dataSources?: Record<string, unknown>;
    eventContainerTemplates: Array<{
        displayName: string;
        id: Uuid;
        provideDisposition?: boolean;
    }>;
    eventTemplates: Array<{
        displayName: string;
        id: Uuid;
    }>;
    forms: Record<string, unknown>;
    iconRegistry?: Record<string, unknown>;
    pages: Record<string, unknown>;
    resolvers?: Record<string, unknown>;
    scaffold: Record<string, unknown>;
    transfer?: WorkspaceTransferUiConfig;
    users?: WorkspaceUsersConfiguration;
};

const namedTemplateValidator = zod.object({
    displayName: zod.string(),
    id: zod.string().uuid(),
});

const eventContainerTemplateRefValidator = namedTemplateValidator.extend({
    provideDisposition: zod.union([zod.boolean(), booleanStringZodValidator]).optional(),
});

const transferPreviewColumnKindValidator = zod.enum([
    "currentOwner",
    "transferredOwner",
    "transferredOn",
    "currentOu",
    "transferredOu",
    "transferReason",
    "customerName",
    "contactNumber",
    "status",
]);

const workspaceTransferUiConfigValidator = zod
    .object({
        bulkPreviewColumns: zod
            .array(
                zod.object({
                    id: zod.string().min(1),
                    kind: transferPreviewColumnKindValidator,
                    label: zod.string().min(1),
                }),
            )
            .optional(),
        bulkSelectMaxTagCount: zod.number().int().positive().optional(),
        listPageLabel: zod.string().min(1),
        previewColumns: zod.array(
            zod.object({
                id: zod.string().min(1),
                kind: transferPreviewColumnKindValidator,
                label: zod.string().min(1),
            }),
        ),
        reasonRequired: zod.boolean().optional(),
        reasons: zod.array(
            zod.object({
                id: zod.string().min(1),
                label: zod.string().min(1),
            }),
        ),
        remarksRequired: zod.boolean().optional(),
        remarksRequiredWhenReasonId: zod.string().min(1).optional(),
        showDimensionSelector: zod.boolean().optional(),
    })
    .partial();

export const workspaceUiConfigConfigurationValidator =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    zod.object({
        columnMapping: zod.record(zod.string(), zod.unknown()),
        components: zod.record(zod.string(), zod.unknown()).optional(),
        ctas: zod.record(zod.string(), zod.unknown()),
        dataSources: zod.record(zod.string(), zod.unknown()).optional(),
        eventContainerTemplates: eventContainerTemplateRefValidator.array(),
        eventTemplates: namedTemplateValidator.array(),
        forms: zod.record(zod.string(), zod.unknown()),
        iconRegistry: zod.record(zod.string(), zod.unknown()).optional(),
        pages: zod.record(zod.string(), zod.unknown()),
        resolvers: zod.record(zod.string(), zod.unknown()).optional(),
        scaffold: zod.record(zod.string(), zod.unknown()),
        transfer: workspaceTransferUiConfigValidator.optional(),
        users: workspaceUsersConfigurationValidator.optional(),
    }) as unknown as zod.ZodType<WorkspaceUiConfigConfiguration>;
